import { useState, useMemo } from "react";
import { Search, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Collapsible } from "@/components/ui/collapsible";
import { SortableTableHead, type SortDirection } from "@/components/shared/SortableTableHead";
import { TableHeadWithTooltip } from "@/components/shared/TableHeadWithTooltip";
import { ProductRow } from "./AsinProductsTable/ProductRow";
import { resolveDateRange } from "@/utils";
import { RankTrackerHeroProduct, RankTrackerKeyword } from "@/types";

interface AsinProductsTableProps {
  selectedDays?: number;
  onDaysChange?: (days: number) => void;
  customDateRange?: { from: Date | undefined; to: Date | undefined };
  onCustomDateChange?: (range: { from: Date | undefined; to: Date | undefined }) => void;
  selectedAsins?: number[];
  onSelectedAsinsChange?: (asins: number[]) => void;
  heroProducts?: RankTrackerHeroProduct[];
  publicData?: { heroProducts: RankTrackerHeroProduct[]; keywordsByHeroProductId: Record<number, RankTrackerKeyword[]> };
  isFetching?: boolean;
}

type AsinSortField = "name" | "keywordCount" | "keywordsInTop10" | "keywordsInTop50" | "avgRank" | "bestRank";

export const AsinProductsTable = ({
  selectedDays = 30,
  customDateRange = { from: undefined, to: undefined },
  selectedAsins = [],
  onSelectedAsinsChange,
  heroProducts: heroProductsProp = [],
  publicData,
  isFetching,
}: AsinProductsTableProps) => {
  const isPublic = publicData !== undefined;
  const heroProducts = isPublic ? publicData.heroProducts : heroProductsProp;
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedAsins, setExpandedAsins] = useState<Set<number>>(new Set());
  const [sortField, setSortField] = useState<AsinSortField | null>("avgRank");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");

  const { dateFrom, dateTo } = useMemo(
    () => resolveDateRange(selectedDays, customDateRange),
    [selectedDays, customDateRange],
  );

  const toggleSort = (field: AsinSortField) => {
    if (sortField !== field) { setSortField(field); setSortDir("desc"); }
    else if (sortDir === "desc") setSortDir("asc");
    else { setSortField(null); setSortDir("desc"); }
  };

  const filteredProducts = useMemo(() => {
    const query = searchQuery.toLowerCase();
    const base = !searchQuery
      ? heroProducts
      : heroProducts.filter(
          (p) => p.name.toLowerCase().includes(query) || p.asin.toLowerCase().includes(query)
        );
    if (!sortField) return base;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...base].sort((a, b) => {
      switch (sortField) {
        case "name": return dir * a.name.localeCompare(b.name);
        case "keywordCount": return dir * ((a.keywordCount ?? 0) - (b.keywordCount ?? 0));
        case "keywordsInTop10": return dir * ((a.top10Count ?? 0) - (b.top10Count ?? 0));
        case "keywordsInTop50": return dir * ((a.top50Count ?? 0) - (b.top50Count ?? 0));
        case "avgRank": return dir * ((a.avgRank ?? 0) - (b.avgRank ?? 0));
        case "bestRank": return dir * ((a.bestRank ?? 0) - (b.bestRank ?? 0));
        default: return 0;
      }
    });
  }, [heroProducts, searchQuery, sortField, sortDir]);

  const toggleAsin = (productId: number) => {
    setExpandedAsins((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const toggleAsinSelection = (productId: number) => {
    if (selectedAsins.includes(productId)) {
      onSelectedAsinsChange(selectedAsins.filter(a => a !== productId));
    } else {
      onSelectedAsinsChange([...selectedAsins, productId]);
    }
  };

  const allSelected = !isPublic && filteredProducts.length > 0 && filteredProducts.every(p => selectedAsins.includes(p.id));
  const someSelected = !isPublic && filteredProducts.some(p => selectedAsins.includes(p.id));
  const isIndeterminate = someSelected && !allSelected;

  return (
    <div className="rounded-b-lg border border-t-0 bg-card">
      <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-border">
        <div className="flex items-center gap-3">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search products or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            Showing {filteredProducts.length} of {heroProducts.length} Products
          </span>
        </div>
      </div>

      <div className="relative">
        {isFetching && !isPublic && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50 backdrop-blur-[1px]">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        <div className="border-t border-border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {!isPublic && (
                  <TableHead className="w-[40px] h-9 px-3 py-2 text-xs text-center" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      className="bg-white"
                      checked={isIndeterminate ? "indeterminate" : allSelected}
                      onCheckedChange={() => {
                        if (allSelected || someSelected) onSelectedAsinsChange?.([]);
                        else onSelectedAsinsChange?.(filteredProducts.map(p => p.id));
                      }}
                    />
                  </TableHead>
                )}
                <TableHead className="w-[40px] h-9 px-3 py-2 text-xs" />
                <SortableTableHead field="name" activeField={sortField} direction={sortDir} onSort={toggleSort} className="w-[280px] h-9 px-3 py-2 text-xs font-semibold text-foreground">
                  Product
                </SortableTableHead>
                <SortableTableHead field="keywordCount" activeField={sortField} direction={sortDir} onSort={toggleSort} tooltip="Total tracked keywords for this ASIN" className="h-9 px-3 py-2 text-xs font-semibold text-foreground text-center">
                  Keywords
                </SortableTableHead>
                <SortableTableHead field="keywordsInTop10" activeField={sortField} direction={sortDir} onSort={toggleSort} tooltip="Keywords ranking in positions 1–10" className="h-9 px-3 py-2 text-xs font-semibold text-foreground text-center">
                  # in Top 10
                </SortableTableHead>
                <SortableTableHead field="keywordsInTop50" activeField={sortField} direction={sortDir} onSort={toggleSort} tooltip="Keywords ranking in positions 1–50" className="h-9 px-3 py-2 text-xs font-semibold text-foreground text-center">
                  # in Top 50
                </SortableTableHead>
                <SortableTableHead field="avgRank" activeField={sortField} direction={sortDir} onSort={toggleSort} tooltip="Average organic rank across all tracked keywords" className="h-9 px-3 py-2 text-xs font-semibold text-foreground text-center">
                  Avg Rank
                </SortableTableHead>
                <SortableTableHead field="bestRank" activeField={sortField} direction={sortDir} onSort={toggleSort} tooltip="Highest ranking position achieved across all keywords" className="h-9 px-3 py-2 text-xs font-semibold text-foreground text-center">
                  Best Rank
                </SortableTableHead>
                <TableHeadWithTooltip className="h-9 px-3 py-2 text-xs font-semibold text-foreground text-center w-[160px]" tooltip="Rank vs. sales trend over 30 days">
                  Sales Correlation
                </TableHeadWithTooltip>
                <TableHead className="w-[60px] h-9 px-3 py-2 text-xs" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isPublic ? 9 : 10} className="h-24 text-center text-muted-foreground">
                    No products found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredProducts.map((product) => (
                  <Collapsible
                    key={product.id}
                    open={expandedAsins.has(product.id)}
                    onOpenChange={() => toggleAsin(product.id)}
                    asChild
                  >
                    <ProductRow
                      product={product}
                      isPublic={isPublic}
                      isExpanded={expandedAsins.has(product.id)}
                      isSelected={selectedAsins.includes(product.id)}
                      onToggleExpand={toggleAsin}
                      onToggleSelect={toggleAsinSelection}
                      publicKeywords={isPublic ? (publicData.keywordsByHeroProductId[product.id] ?? []) : undefined}
                      dateFrom={dateFrom}
                      dateTo={dateTo}
                    />
                  </Collapsible>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
